import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  assignAssessor as assignAssessorService,
  acceptAssessment as acceptAssessmentService,
  beginAssessment as beginAssessmentService,
  rejectAssessment as rejectAssessmentService,
  updateAssessor as updateAssessorService,
  submitAssessment as submitAssessmentService,
} from './workflow';
import { WorkflowAssessmentResponse } from '../interfaces/workflow.interface';
import { toWorkflowForStore } from '../utils/workflowStorageUtils';
import { upsertWorkflow } from '../app/store/workflowSlice';
import { createAssessment } from '../app/store/assessmentSlice';
import { setAssessmentStatus } from '../app/store/questionnaireAnswerSlice';

export const assignAssessor = createAsyncThunk(
  'workflow/assignAssessor',
  async (
    request: {
      assessorID: number;
      workflowId: number;
      assignedAssessorId: number;
      isOnPremise: boolean;
      isCloud: boolean;
      isInPerson: boolean;
    },
    thunkAPI,
  ) => {
    try {
      const responseWorkflow: WorkflowAssessmentResponse = await assignAssessorService(request);
      const workflow = toWorkflowForStore(responseWorkflow);

      thunkAPI.dispatch(
        createAssessment.fulfilled(
          responseWorkflow.assessment,
          thunkAPI.requestId,
          { ...responseWorkflow.assessment },
        ),
      );

      thunkAPI.dispatch(upsertWorkflow(workflow));

      return workflow;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const acceptAssessment = createAsyncThunk(
  'workflow/acceptAssessment',
  async (workflowId: number, thunkAPI) => {
    try {
      const responseWorkflow: WorkflowAssessmentResponse = await acceptAssessmentService(workflowId);
      const workflow = toWorkflowForStore(responseWorkflow);

      thunkAPI.dispatch(upsertWorkflow(workflow));

      return { workflow, assessment: responseWorkflow.assessment };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const beginAssessment = createAsyncThunk(
  'workflow/beginAssessment',
  async (workflowId: number, thunkAPI) => {
    try {
      const responseWorkflow: WorkflowAssessmentResponse = await beginAssessmentService(workflowId);
      const workflow = toWorkflowForStore(responseWorkflow);

      thunkAPI.dispatch(upsertWorkflow(workflow));

      return { workflow, assessmentSurvey: responseWorkflow.assessmentSurvey };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const rejectAssessment = createAsyncThunk(
  'workflow/rejectAssessment',
  async (workflowId: number, thunkAPI) => {
    try {
      const responseWorkflow: WorkflowAssessmentResponse = await rejectAssessmentService(workflowId);
      const workflow = toWorkflowForStore(responseWorkflow);

      thunkAPI.dispatch(upsertWorkflow(workflow));

      return workflow;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const updateAssessor = createAsyncThunk(
  'workflow/updateAssessor',
  async (
    request: {
      assessorID: number;
      workflowId: number;
      assignedAssessorId: number;
      isOnPremise: boolean;
      isCloud: boolean;
      isInPerson: boolean;
    },
    thunkAPI,
  ) => {
    try {
      const responseWorkflow: WorkflowAssessmentResponse = await updateAssessorService(request);
      const workflow = toWorkflowForStore(responseWorkflow);

      thunkAPI.dispatch(upsertWorkflow(workflow));

      return workflow;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);

export const submitAssessment = createAsyncThunk(
  'workflow/submitAssessment',
  async (workflowId: number, thunkAPI) => {
    try {
      const responseWorkflow: WorkflowAssessmentResponse = await submitAssessmentService(workflowId);
      const workflow = toWorkflowForStore(responseWorkflow);

      thunkAPI.dispatch(setAssessmentStatus('Submitted'));

      thunkAPI.dispatch(upsertWorkflow(workflow));

      return { workflow, assessmentSurvey: responseWorkflow.assessmentSurvey };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
);
